/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: useAvatarUpload.ts
 🗓️ Created: 2025-01-XX
 🧩 Description:
    Custom hook for handling avatar upload and removal.

 🔁 Last Updated: 2025-01-XX by Rodrigo Cuadra
────────────────────────────────────────────────────────────────*/

import { useState } from 'react';
import { useAuthStore } from '../../../store/auth';
import { useNotifications } from '../../../hooks/useNotifications';
import { useI18n } from '../../../hooks/useI18n';
import { uploadAvatar } from '../../../lib/api';

/**
 * Helper function to construct full avatar URL
 */
const getFullAvatarUrl = (avatarPath: string | null): string | null => {
  if (!avatarPath) return null;
  return avatarPath.startsWith('http') ? avatarPath : `${avatarPath}`;
};

/**
 * Maximum file size: 5MB
 */
const MAX_FILE_SIZE = 5 * 1024 * 1024;

/**
 * Custom hook for avatar upload
 */
export function useAvatarUpload(
  userAvatar: string | null,
  setAvatar: (avatar: string | null) => void
) {
  const { t } = useI18n();
  const { user } = useAuthStore();
  const { addNotification } = useNotifications();
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const handleAvatarSelect = async () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = async (event) => {
      const file = (event.target as HTMLInputElement).files?.[0];
      if (!file) return;

      // Validate file size
      if (file.size > MAX_FILE_SIZE) {
        addNotification({ message: t('profile.fileSizeError'), type: 'error' });
        return;
      }

      // Validate file type
      if (!file.type.startsWith('image/')) {
        addNotification({ message: t('profile.fileTypeError'), type: 'error' });
        return;
      }

      setSelectedFile(file);

      // Show preview
      const reader = new FileReader();
      reader.onload = (event) => {
        const result = event.target?.result as string;
        setAvatar(result);
      };
      reader.readAsDataURL(file);

      // Upload immediately if user exists
      if (user?.id) {
        setUploading(true);
        try {
          const tenantSlug = user?.tenant?.slug || 'default';
          const result = await uploadAvatar(file, user.id.toString(), tenantSlug);
          setAvatar(getFullAvatarUrl(result.data.avatarUrl));
          addNotification({ message: t('profile.avatarUploaded'), type: 'success' });
        } catch (error) {
          addNotification({ 
            message: error.response?.data?.message || t('profile.avatarUploadError'), 
            type: 'error' 
          });
          setSelectedFile(null);
          // Reset avatar to original if upload failed
          setAvatar(getFullAvatarUrl(userAvatar));
        } finally {
          setUploading(false);
        }
      }
    };
    input.click();
  };

  const handleAvatarRemove = () => {
    setAvatar(null);
    setSelectedFile(null);
  };

  return {
    uploading,
    selectedFile,
    handleAvatarSelect,
    handleAvatarRemove
  };
}

